import React, { useMemo, useState } from 'react';
import { EARNINGS_SURPRISE_RANGE_OPTIONS } from './types';
import type { EarningsSurpriseWidgetInstanceConfig, EarningsSurpriseData } from './types';
import { formatPercent, formatFloat, SEMANTIC_CHART_COLORS } from '../../../design';
import { useDataProviderWidget } from '../../../blocks/hooks';
import { ChartWidget, ChartRangeSelector, KeyMetricBadge, sliceByRows, redundantRangeValues } from '../../../blocks/chart-widget';
import type { ChartSpec } from '../../../blocks/chart-widget';
import type { BarChartSeries } from '../../../blocks/charts';

// Sample data shown during dashboard construction (no asset assigned).
const SAMPLE_DATA: EarningsSurpriseData = {
  ticker: 'SAMPLE',
  last_period: 'Q2 2026',
  last_actual_eps: 2.01,
  last_estimate_eps: 1.99,
  last_surprise_pct: 1.09,
  // Most-recent-first, matching the backend contract the renderer reverses.
  surprise_history: [
    { date: '2026-03-31', label: 'Q2 2026', actual: 2.01, estimate: 1.99, surprise_pct: 1.09 },
    { date: '2025-12-31', label: 'Q1 2026', actual: 2.40, estimate: 2.35, surprise_pct: 2.1 },
    { date: '2025-09-30', label: 'Q4 2025', actual: 1.65, estimate: 1.60, surprise_pct: 3.1 },
    { date: '2025-06-30', label: 'Q3 2025', actual: 1.40, estimate: 1.43, surprise_pct: -2.1 },
  ],
};

interface Props {
  config: EarningsSurpriseWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: unknown) => void;
  readOnly?: boolean;
  widgetInstanceId?: string;
}

const EarningsSurpriseWidgetRenderer: React.FC<Props> = ({
  config,
  dashboardData = {},
  widgetInstanceId,
}) => {
  const uid = dashboardData?.assetDataUid as string | undefined;
  const isPreview = !uid;

  const { data, loading, error, refetch } = useDataProviderWidget<EarningsSurpriseData>(
    'earnings_surprise',
    uid,
    config as unknown as Record<string, unknown>,
    { enabled: !!uid, widgetInstanceId },
  );

  const d = uid ? data : SAMPLE_DATA;
  const [range, setRange] = useState<string>(config.defaultRange ?? 'ALL');

  // Backend returns most-recent-first; reverse for a left-to-right timeline.
  const fullChartData = useMemo(() => {
    const hist = d?.surprise_history ?? [];
    return [...hist]
      .reverse()
      .filter((r) => typeof r.surprise_pct === 'number')
      .map((r) => ({ label: r.label ?? r.date, surprise_pct: r.surprise_pct as number }));
  }, [d]);

  const disabledRanges = useMemo(
    () => redundantRangeValues(EARNINGS_SURPRISE_RANGE_OPTIONS, fullChartData),
    [fullChartData],
  );

  const chartData = useMemo(() => {
    const rows = EARNINGS_SURPRISE_RANGE_OPTIONS.find((o) => o.value === range)?.rows;
    return sliceByRows(fullChartData, rows);
  }, [fullChartData, range]);

  // Semantic colors: beats green, misses red — surprise direction IS the data.
  const series = useMemo<BarChartSeries[]>(() => [{
    yKey: 'surprise_pct',
    yName: 'Surprise %',
    fillBySign: {
      positive: SEMANTIC_CHART_COLORS.benchmark,
      negative: SEMANTIC_CHART_COLORS.risk,
    },
  }], []);

  const spec: ChartSpec = {
    chartKind: 'bar',
    rows: chartData,
    xKey: 'label',
    barSeries: series,
    showEndValueTags: false,
    yLabelFormatter: (params) => formatPercent(Number(params.value), 0),
    xTitle: 'Period',
    yTitles: { a: 'Surprise %' },
    footnote: 'Source: FMP',
    emptyMessage: 'No earnings history available',
  };

  const header = (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-neutral-100 shrink-0">
      <div className="flex items-center gap-2 mr-1">
        <span className="text-sm font-semibold text-neutral-800">Earnings Surprise</span>
        {d?.last_period && <span className="text-[11px] uppercase tracking-wide text-neutral-400">{d.last_period}</span>}
      </div>
      <KeyMetricBadge
        label="EPS A/E"
        value={`${formatFloat(d?.last_actual_eps ?? null, 2)} / ${formatFloat(d?.last_estimate_eps ?? null, 2)}`}
      />
      <KeyMetricBadge
        label="Surprise"
        value={d?.last_surprise_pct == null ? '--' : `${d.last_surprise_pct >= 0 ? '+' : ''}${formatPercent(d.last_surprise_pct, 1)}`}
      />
      <ChartRangeSelector
        className="ml-auto"
        options={EARNINGS_SURPRISE_RANGE_OPTIONS}
        value={range}
        onChange={setRange}
        disabledValues={disabledRanges}
      />
    </div>
  );

  return (
    <ChartWidget
      loading={loading}
      error={error}
      refetch={refetch}
      isPreview={isPreview}
      spec={spec}
      header={header}
    />
  );
};

export default EarningsSurpriseWidgetRenderer;
